import { CancelRounded, CheckCircleRounded } from "@mui/icons-material";
import { Alert, Button, Skeleton } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  canEditQualityEventRecord,
  isQualityEventStepUnlocked,
  prepareQualityEventChanges,
} from "src/components/QualityEvents/quality-event-draft";
import { QualityEventReviewForm } from "src/components/QualityEvents/QualityEventReviewForm";
import { useGetUser, useGetUsersByOrg } from "src/hooks/queries";
import { usePrepareQualityEventDraft } from "src/hooks/queries/quality-event-mutations";
import {
  useGetQualityEventDefinition,
  useGetQualityEventRecord,
} from "src/hooks/queries/quality-events";
import { qualityEventErrorMessage } from "src/services/quality-events";
import { OrgScopedBaseUser, User } from "src/stores/models/user";
import {
  QualityEventDefinition,
  QualityEventDraft,
  QualityEventProposal,
  qualityEventProposalSchema,
  QualityEventRecord,
  QualityEventStep,
} from "src/types/quality-events";
import { useHumanInTheLoop } from "../../HumanInTheLoopContext";
import { ToolCallName } from "../utils/tool-calls";
import { keyToLabel } from "../utils/utils";
import { EditToolCallFrame } from "./EditToolCallFrame";

const EDIT_TOOL_TITLE = keyToLabel(ToolCallName.EDIT_QUALITY_EVENT_RECORD);

const QualityEventDraftDecisionControls = ({
  actionIndex,
  pending,
  onApprove,
}: {
  actionIndex: number;
  pending: boolean;
  onApprove?: () => void;
}) => {
  const humanInTheLoop = useHumanInTheLoop();
  if (!humanInTheLoop?.request) return null;
  const allowedDecisions =
    humanInTheLoop.request.review_configs[actionIndex]?.allowed_decisions;
  const disabled = pending || !!humanInTheLoop.decisions[actionIndex];
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div
        aria-label="Review decision actions"
        role="group"
        className="flex shrink-0 items-center gap-2 whitespace-nowrap"
      >
        {allowedDecisions?.includes("reject") && (
          <Button
            variant="contained"
            color="error"
            sx={{ backgroundColor: "#d32f2f" }}
            size="small"
            disabled={disabled}
            startIcon={<CancelRounded />}
            onClick={() =>
              humanInTheLoop.setDecision(actionIndex, { type: "reject" })
            }
          >
            Reject
          </Button>
        )}
        {allowedDecisions?.includes("approve") && (
          <Button
            variant="contained"
            color="success"
            sx={{ backgroundColor: "#15803d" }}
            size="small"
            disabled={disabled || !onApprove}
            startIcon={<CheckCircleRounded />}
            onClick={onApprove}
          >
            Approve
          </Button>
        )}
      </div>
    </div>
  );
};

const InactiveQualityEventRecordTool = ({
  isLoading,
  decision,
}: {
  isLoading: boolean;
  decision?: "approve" | "reject" | "edit";
}) => (
  <EditToolCallFrame
    toolName={ToolCallName.EDIT_QUALITY_EVENT_RECORD}
    title={EDIT_TOOL_TITLE}
    label={EDIT_TOOL_TITLE}
    isLoading={decision !== "reject" && isLoading}
    hasContent={false}
    decision={decision}
  >
    {null}
  </EditToolCallFrame>
);

export const EditQualityEventRecordTool = ({
  args,
  isLoading,
  actionIndex,
  decision,
}: {
  args: Record<string, unknown>;
  isLoading: boolean;
  actionIndex?: number;
  decision?: "approve" | "reject" | "edit";
}) => {
  const { orgId = "" } = useParams();
  const humanInTheLoop = useHumanInTheLoop();
  const inactiveProposal =
    actionIndex === undefined ||
    humanInTheLoop?.request?.action_requests[actionIndex]?.name !==
      ToolCallName.EDIT_QUALITY_EVENT_RECORD;
  if (inactiveProposal)
    return (
      <InactiveQualityEventRecordTool
        isLoading={isLoading}
        decision={decision}
      />
    );

  const parsed = qualityEventProposalSchema.safeParse(args);
  return (
    <QualityEventRecordDraftProposal
      orgId={orgId}
      proposal={parsed.success ? parsed.data : null}
      actionIndex={actionIndex}
    />
  );
};

const getQualityEventDraftWarning = ({
  proposal,
  definition,
  record,
  step,
  users,
  usersError,
  user,
}: {
  proposal: QualityEventProposal | null;
  definition?: QualityEventDefinition;
  record?: QualityEventRecord;
  step?: QualityEventStep;
  users?: OrgScopedBaseUser[];
  usersError: boolean;
  user: User | undefined;
}) => {
  if (!proposal)
    return "The Agent could not prepare this quality event record draft. Reject it and ask it to try again.";
  if (!proposal.recordId && proposal.steps != null)
    return "Creation can only include the first step. Reject this draft and ask the Agent to prepare it again.";
  if (!definition || !users || usersError)
    return "Unable to load this quality event record draft. Reject it and reopen the record.";
  if (
    !step ||
    (record
      ? record.qualityEventKey !== proposal.qualityEventKey ||
        !canEditQualityEventRecord(record, user) ||
        !isQualityEventStepUnlocked(record, step.key)
      : definition.definition.steps[0]?.key !== step.key)
  )
    return "This step is locked or you do not have permission to edit it. Open the record to see the available steps.";
  if (
    proposal.expectedVersion != null &&
    record &&
    proposal.expectedVersion !== record.version
  )
    return "This record changed after the proposal was prepared. Reject it and ask the Agent to reload the record.";
  return null;
};

const QualityEventRecordDraftWarning = ({
  warning,
  actionIndex,
  pending,
}: {
  warning: string;
  actionIndex: number;
  pending: boolean;
}) => (
  <EditToolCallFrame
    toolName={ToolCallName.EDIT_QUALITY_EVENT_RECORD}
    title={EDIT_TOOL_TITLE}
    label={EDIT_TOOL_TITLE}
    isLoading={pending}
    controls={
      <QualityEventDraftDecisionControls
        actionIndex={actionIndex}
        pending={pending}
      />
    }
  >
    <Alert severity="warning">{warning}</Alert>
  </EditToolCallFrame>
);

const QualityEventRecordDraftReview = ({
  orgId,
  proposal,
  definition,
  record,
  step,
  users,
  actionIndex,
}: {
  orgId: string;
  proposal: QualityEventProposal;
  definition: QualityEventDefinition;
  record?: QualityEventRecord;
  step: QualityEventStep;
  users: OrgScopedBaseUser[];
  actionIndex: number;
}) => {
  const humanInTheLoop = useHumanInTheLoop();
  const prepare = usePrepareQualityEventDraft({ orgId });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pending = submitting || !!humanInTheLoop?.isLoading;
  const steps = definition.definition.steps;

  const approveDraft = async (drafts: Record<string, QualityEventDraft>) => {
    setSubmitting(true);
    setError(null);
    try {
      const primary = drafts[proposal.stepKey];
      const changes = await prepareQualityEventChanges(
        drafts,
        proposal.stepKey,
        steps[0]?.key ?? proposal.stepKey,
        prepare.mutateAsync
      );
      humanInTheLoop?.setDecision(actionIndex, {
        type: "edit",
        edited_action: {
          name: ToolCallName.EDIT_QUALITY_EVENT_RECORD,
          args: {
            qualityEventKey: proposal.qualityEventKey,
            stepKey: proposal.stepKey,
            ...(proposal.recordId
              ? {
                  recordId: proposal.recordId,
                  expectedVersion: primary.expectedVersion,
                }
              : { id: primary.id }),
            ...changes,
            ...(proposal.missingRequirements?.length
              ? {
                  missingRequirements: proposal.missingRequirements,
                }
              : {}),
          },
        },
      });
    } catch (caught) {
      setError(qualityEventErrorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <QualityEventReviewForm
      orgId={orgId}
      proposal={proposal}
      definition={definition}
      record={record}
      step={step}
      users={users}
      pending={pending}
      error={error}
      missingRequirements={proposal.missingRequirements ?? []}
      actionIndex={actionIndex}
      renderReview={({ fields, drafts }) => (
        <EditToolCallFrame
          toolName={ToolCallName.EDIT_QUALITY_EVENT_RECORD}
          title={EDIT_TOOL_TITLE}
          label={EDIT_TOOL_TITLE}
          isLoading={pending}
          controls={
            <QualityEventDraftDecisionControls
              actionIndex={actionIndex}
              pending={pending}
              onApprove={() => void approveDraft(drafts)}
            />
          }
        >
          {fields}
        </EditToolCallFrame>
      )}
    />
  );
};

const QualityEventRecordDraftProposal = ({
  orgId,
  proposal,
  actionIndex,
}: {
  orgId: string;
  proposal: QualityEventProposal | null;
  actionIndex: number;
}) => {
  const humanInTheLoop = useHumanInTheLoop();
  // Editing uses the record's definition; only creation needs a catalog read.
  const definition = useGetQualityEventDefinition({
    orgId,
    qualityEventKey: proposal?.recordId
      ? ""
      : (proposal?.qualityEventKey ?? ""),
  });
  const record = useGetQualityEventRecord({
    orgId,
    recordId: proposal?.recordId ?? "",
  });
  const users = useGetUsersByOrg({ orgId });
  const user = useGetUser();
  const qualityEventDefinition = proposal?.recordId
    ? record.data?.definition
    : definition.data;
  const steps = qualityEventDefinition?.definition.steps;
  const step = steps?.find((step) => step.key === proposal?.stepKey);
  const loading =
    !!proposal &&
    (users.isLoading ||
      user.isLoading ||
      (proposal.recordId ? record.isLoading : definition.isLoading));
  if (loading) return <Skeleton variant="rounded" height={200} />;

  const warning = getQualityEventDraftWarning({
    proposal,
    definition: qualityEventDefinition,
    record: record.data,
    step,
    users: users.data,
    usersError: users.isError,
    user: user.data,
  });
  if (warning)
    return (
      <QualityEventRecordDraftWarning
        warning={warning}
        actionIndex={actionIndex}
        pending={!!humanInTheLoop?.isLoading}
      />
    );

  if (!proposal || !qualityEventDefinition || !step || !users.data) return null;
  return (
    <QualityEventRecordDraftReview
      orgId={orgId}
      proposal={proposal}
      definition={qualityEventDefinition}
      record={record.data}
      step={step}
      users={users.data}
      actionIndex={actionIndex}
    />
  );
};
